// @flow
import { TrashSimple } from '@phosphor-icons/react'
import { Button } from 'antd'
import Spinner from 'components/Spinner'
import { useLoadCustomer } from 'queries/customers/useLoadCustomer'
import { useDeleteRoutines } from 'queries/routines/useDeleteRoutines'
import { useLoadRoutines } from 'queries/routines/useLoadRoutines'
import * as React from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import type { RoutineType } from 'types/routines'
import Flex from 'ui/atoms/Flex'
import Space from 'ui/atoms/Space'
import Table from 'ui/atoms/Table'
import TableEmptyMessage from 'ui/table/TableEmptyMessage'
import Theme from 'ui/theme'
import { ZentakeContext } from 'utils/ZentakeContext'

import Tooltip from '@/ui/atoms/Tooltip'

import CreateEditRoutineModal from './components/CreateEditRoutineModal'

const Wrapper = styled.div`
  background-color: white;
  padding: ${Theme.spacing.large};
  overflow: auto;
`

function Routines(): React.Node {
  const { clientId } = useParams()
  const { data: clientInDetail } = useLoadCustomer(clientId)
  const {
    data: routines,
    isLoading,
    isFetching,
  } = useLoadRoutines(clientInDetail?.id || '')
  const { deleteRoutine } = useDeleteRoutines()
  const routinesLoading = isLoading || isFetching
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [selectedRoutine, setSelectedRoutine] =
    React.useState<RoutineType | void>(undefined)
  const { setAlertConfig } = React.useContext(ZentakeContext)
  const repeatTypeOptions = React.useCallback((routine: RoutineType) => {
    const value = {
      Daily: 'Day',
      Weekly: 'Week',
      Monthly: 'Month',
      Yearly: 'Year',
    }[routine.repeat_type]

    if (routine.repeat_interval > 1) {
      return value + 's'
    }

    return value
  }, [])
  const status = React.useCallback((routine: RoutineType) => {
    if (!routine.occurrences || routine.occurrences.length === 0) {
      return '0 of 0 sent'
    }

    if (routine.occurrences.some((o) => o.status === 'Failed')) {
      return 'Failed'
    }

    const sent = routine.occurrences?.filter(
      (o) => o.status === 'Processed'
    ).length
    // $FlowFixMe
    return `${sent} of ${routine.occurrences.length} sent`
  }, [])
  const startDate = React.useCallback((routine: RoutineType) => {
    const [year, month, day] = routine?.start_date?.split('-') || []
    return `${month}-${day}-${year}`
  }, [])

  const showDeleteConfirmation = React.useCallback(
    async () =>
      new Promise((resolve) =>
        setAlertConfig({
          title: 'Are you sure you want to delete this Routine?',
          description:
            'Note: This will not delete any forms or packets associated with this Routine.',
          buttons: [
            {
              title: 'Delete',
              style: 'primary',
              danger: true,
              onPress: () => resolve(true),
            },
            {
              title: 'Cancel',
              onPress: () => resolve(false),
            },
          ],
          canDismiss: false,
        })
      ),
    [setAlertConfig]
  )

  const handleDelete = React.useCallback(
    async (routineId) => {
      const result = await showDeleteConfirmation()
      if (result) {
        try {
          await deleteRoutine({
            customerId: clientInDetail?.id || '',
            id: routineId,
          })
          toast.success('Scheduled Form deleted successfully')
        } catch (error) {
          toast.error('Error deleting Scheduled Form')
        }
      }
    },
    [clientInDetail?.id, deleteRoutine, showDeleteConfirmation]
  )

  const renderTable = React.useCallback(() => {
    return (
      <Table>
        <Table.Head
          columns={[
            'Form',
            'Email/Phone',
            'Repeat Every',
            'Start Date',
            'Status',
            '',
          ]}
        />
        <Table.Body>
          {routines.map((routine) => (
            <Table.Row key={routine?.uid}>
              <Table.Column $truncate style={{ maxWidth: 240 }}>
                {routine?.form_name ||
                  routine?.packet_name ||
                  routine?.document_name ||
                  'N/A'}
              </Table.Column>
              <Table.Column>
                {routine.customer_email ||
                  routine.customer_phone_number ||
                  'N/A'}
              </Table.Column>
              <Table.Column>
                Every {routine.repeat_interval} {repeatTypeOptions(routine)}
              </Table.Column>
              <Table.Column>{startDate(routine)}</Table.Column>
              <Table.Column>{status(routine)}</Table.Column>
              <Table.Column>
                <Flex $horizontalSpacing="small">
                  {/* <Button
                    onClick={() => {
                      setSelectedRoutine(routine)
                      setIsModalOpen(true)
                    }}
                  >
                    <Icons.Pencil />
                  </Button> */}
                  <Tooltip mouseEnterDelay={0.35} title="Edit">
                    <Button
                      className="w-[fit-content] !px-4"
                      onClick={() => handleDelete(routine?.uid)}
                    >
                      <TrashSimple className="text-xl" />
                    </Button>
                  </Tooltip>
                </Flex>
              </Table.Column>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    )
  }, [routines, repeatTypeOptions, startDate, status, handleDelete])

  const renderContent = React.useCallback(() => {
    if (routinesLoading) {
      return (
        <>
          <Spinner color="onLight" size={20} />
          <span className="ml-2">Loading...</span>
        </>
      )
    }

    if (!routines || routines.length === 0) {
      return <TableEmptyMessage>No Results Found</TableEmptyMessage>
    }

    return renderTable()
  }, [routinesLoading, routines, renderTable])

  return (
    <Wrapper className="rounded-bl rounded-br rounded-tr border border-gray-50">
      <Space>
        <Flex $horizontalSpacing="large">
          <Button
            disabled={routinesLoading}
            onClick={() => {
              setIsModalOpen(true)
            }}
            type="primary"
          >
            Create Schedule
          </Button>
        </Flex>
        {renderContent()}
        {isModalOpen && (
          <CreateEditRoutineModal
            onClose={() => {
              setIsModalOpen(false)
              setSelectedRoutine(undefined)
            }}
            routine={selectedRoutine}
          />
        )}
      </Space>
    </Wrapper>
  )
}

export default Routines
